import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getContactList,
  useNumberDetails,
  useUserDetails
} from "redux/slice/faxSlice";
import { useAppDispatch } from "redux/hooks";
import { sendFax } from "redux/slice/faxSlice";
import PdfViewer from "components/ui-component/pdf-viewer/PdfViewer";
import "../../../index.css";

import { useAuth0 } from "@auth0/auth0-react";
import { useNumber } from "redux/slice/commanSlice";
import { AutoComplete, CheckboxChangeEvent, Col, Form, message, RadioChangeEvent, Row, Tag, Typography, Upload, UploadProps, Tooltip } from "antd";
import { createContact } from "redux/slice/faxSlice";

import { checkCoverPage, getCoverPages, useCoverPages } from "redux/slice/coverPageSlice";
import { ComposeFaxHeader, ComposeFaxStyle } from "./ComposeFax.style";
import { Button, CheckBox, Input, Radio, SelectBar } from "components/ui-component";
import AddSquareIcon from "components/Icons/AddSquareIcon";
import type { UploadFile } from "antd/es/upload/interface";
import debounce from 'lodash/debounce';
import RemoveSquareIcon from "components/Icons/RemoveSquareIcon";
import type { FormInstance } from 'antd/es/form';
import { renderRecipientOptionLabel } from "components/Helper/RecipientOptionRenderer";

// Define a type for recipient
interface Recipient {
  recipientName?: string;
  recipientFaxNumber?: string;
  searchInputName?: string;
  searchInputNumber?: string;
  contactId?: string;
}

const ComposeFax = () => {
  const latestSearchValue = useRef<string>("");
  const { user } = useAuth0();
  const number = useNumber();
  const coverPage = useCoverPages();
  const userDetails = useUserDetails();
  const numberDetails = useNumberDetails();
  const coverpageOptions = [
    { label: "None", value: "" },
    ...coverPage?.coverpages?.map((coverPage: any) => ({
      label: coverPage.description,
      value: coverPage.id,
    }))
  ];

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const initialValues = {
    username: user?.email,
    from_number: number,
    to_number: "",
    notes: "",
    fax_file: [] as UploadFile<any>[],
    cover_subject: "",
    cover_body: "",
    cover_to_name: "",
    cover_from_name: user?.name,
    addCover: false,
    cover_page: [],
    selectedCover: "",
    isUpload: false,
    recipients: [
      {
        recipientName: undefined,
        recipientFaxNumber: undefined,
        searchInputName: undefined,
        searchInputNumber: undefined,
        contactId: undefined,
      } as Recipient
    ],
  }; 
    const [recipientOptions, setRecipientOptions] = useState<
      { contact_id:string; firstname: string; lastname: string; fax_number: string }[]
    >([]);
    const [showCheckbox, setShowCheckbox] = React.useState<boolean[]>([]);

    const [checkCoverPageResponse, setCheckCoverPageResponse] = useState<any>(null);
    const [values, setValues] = React.useState<typeof initialValues>(initialValues);
    const [form] = Form.useForm();
    const [isSending, setIsSending] = React.useState(false);

    // Utility to generate UploadProps for any field
    const getUploadProps = (field: 'fax_file' | 'cover_page'): UploadProps => ({
      multiple: true,
      accept: "application/pdf,application/vnd.ms-excel,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/tiff,image/tif",
      beforeUpload: (file) => {
        return false;
      },
      onChange: (info) => {
        setValues(prevData => ({
          ...prevData,
          [field]: info.fileList,
        }));
        form.setFieldsValue({
          [field]: info.fileList,
        });
      },
      fileList: values[field] as any[],
    });

    const faxFileUploadProps = getUploadProps('fax_file');
    const coverPageUploadProps = getUploadProps('cover_page');

    useEffect(() => {
      const params = {
        page: 1,
        limit: 1000,
        my_coverpages: true,
      }
      dispatch(getCoverPages(params));

      const fetchCoverPages = async () => {
        await dispatch(getCoverPages(params)).unwrap();
      }
      fetchCoverPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    

  // Effect to call checkCoverPage API using async/await
  useEffect(() => {
    const checkCoverPageStatus = async () => {
      if (userDetails && numberDetails && number) {
        const userId = userDetails.user_id;
        let numberId = null;

        // Find the corresponding did_id (number_id) from assigned_number_data
        if (userDetails.assigned_number_data && numberDetails.did) {
          const foundNumberData = userDetails.assigned_number_data.find(
            (item: any) => item.did === numberDetails.did
          );
          if (foundNumberData) {
            numberId = foundNumberData.did_id;
          }
        }

        if (userId !== undefined && numberId !== null) {
          const apiParams = {
            user_id: userId,
            number_id: numberId,
          };
          try {
            const res = await dispatch(checkCoverPage(apiParams)).unwrap();
            setCheckCoverPageResponse(res);
          } catch (error: any) {
            console.error("checkCoverPage error:", error);
          }
        } else {
          console.warn("Could not find user_id or number_id for checkCoverPage API.");
        }
      }
    };

    checkCoverPageStatus();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails, numberDetails, number]); // Depend on userDetails, numberDetails, and number

  // Helper to create FormData payload for a recipient
  const createFaxPayload = (values: any, recipient: any) => {
    const {
      username,
      from_number,
      notes,
      fax_file,
      cover_subject,
      cover_body,
      cover_from_name,
      cover_page,
      selectedCover,
    } = values;

    const payload = new FormData();
    payload.append("username", username);
    payload.append("from_number", from_number);
    payload.append("notes", notes);
    if (!selectedCover) {
      payload.append("cover_subject", cover_subject);
      payload.append("cover_body", cover_body);
    }
    payload.append("cover_from_name", cover_from_name);
    payload.append("cover_to_name", recipient.recipientName ?? "");
    payload.append("to_number", recipient.recipientFaxNumber);

    for (let i = 0; i < fax_file?.length; i++) {
      payload.append("fax_file", fax_file[i]?.originFileObj);
    }
    for (let i = 0; i < cover_page?.length; i++) {
      payload.append("cover_page", cover_page[i]?.originFileObj);
    }
    if (selectedCover) {
      payload.append("selected_cover", selectedCover);
    }
    return payload;
  };

  const onSubmit = async (formValues: any) => {
    let allSuccess = true;
    setIsSending(true);
    try {
      for (const recipient of values.recipients) {
        const payload = createFaxPayload(formValues, recipient);
        try {
          const res: any = await dispatch(sendFax(payload)).unwrap();
          //  this error message may contain an object structure in a string. should be included in future fix in the backend
          if (res?.status !== "Accepted") {
            allSuccess = false;
            const raw = res?.message || `Fax failed for ${recipient.recipientName}`;
            const inner = raw.match(/'message':\s*"([^"]+)"/);
            message.error(inner ? inner[1] : raw);
          }
        } catch (error) {
          allSuccess = false;
          console.warn("Fax error:", error);
        }
      }
      if (allSuccess) {
        message.success("Fax sent successfully");
        navigate(-1);
      }
    } catch (error: any) {
      console.log("Sent fax err:---->", error?.message);
    } finally {
      setIsSending(false);
    }
  };

  const updateCheckboxVisibility = (index: number, visibility: boolean) => {
    const newCheckboxState = [...showCheckbox];
    newCheckboxState[index] = visibility;
    setShowCheckbox(newCheckboxState);
  };

  const insertContact = async (fax_number: any, recipient: any, idx: number) => {
      try {
        const payload = {  
          "firstname": recipient,
          "lastname":"",
          "fax_number":fax_number,
          "notes":"",
        }
        const res: any = await dispatch( createContact( payload ) ).unwrap()
        if ( res ) {
          message.success( "Contact created successfully" )
        }
      } catch ( error: any ) {
        console.log( "edit sent fax err: --->", error?.message )
      }
    updateCheckboxVisibility(idx, false);
  }

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    const { name, checked } = e.target;
    name &&
      setValues((prevData) => ({
        ...prevData,
        [name]: checked,
      }));
  };

  const handleRadioChange = (e: RadioChangeEvent) => {
    const { value } = e.target;
      setValues((prevData) => ({
        ...prevData,
        isUpload: value,
      }));
  };

  const onGetContacts = debounce(
    async (
      params: { name_query?: string; fax_number?: string },
      searchValue: string,
      index: number
    ) => {
      // If the search value is empty, do not call the API
      const value = params.name_query ?? params.fax_number ?? "";
      if (!value || value.trim() === "") {
        setRecipientOptions([]);
        return;
      }
      try {
        const response: any = await dispatch(getContactList(params)).unwrap();
        // Only update if the search value matches the latest
        if (searchValue === latestSearchValue.current) {
          if (response?.contacts) setRecipientOptions(response?.contacts);
        }

        if (response?.contacts?.length > 0) {
          updateCheckboxVisibility(index, false);
        } else if (Object.keys(params)[0] === "fax_number") {
          updateCheckboxVisibility(index, true);
        }
      } catch (error: any) {
        console.log('[ERROR]', error?.message);
      }
    },
    500
  );

  const handleSelectContact = (
    contactId: string,
    index: number
  ) => {
    const contact = (recipientOptions ?? [])?.find(
      (option) => option.contact_id === contactId
    );
    if (!contact) return;
    const fullName = `${contact.firstname} ${contact.lastname}`.trim();
    const faxNumber = contact.fax_number;

    const updatedRecipients = form.getFieldValue("recipients").map(
      (item: any, i: number) =>
        i === index
          ? { ...item, recipientName: fullName, recipientFaxNumber: faxNumber }
          : item
    );
    form.setFieldsValue({ recipients: updatedRecipients });

    setValues((prev) => {
      const newRecipients = [...prev.recipients];
      newRecipients[index] = {
        ...newRecipients[index],
        recipientFaxNumber: faxNumber,
        recipientName: fullName,
        contactId,
      };
      return { ...prev, recipients: newRecipients };
    });

    form
      .validateFields([
        ["recipients", index, "recipientName"],
        ["recipients", index, "recipientFaxNumber"],
      ])
      .catch(() => {});
  };

  const handleRecipientInput = (
    newValue: string,
    type: "name" | "number",
    index: number
  ) => {
    // Update form and component state with the typed value
    const updatedRecipients = form.getFieldValue("recipients").map(
      (item: any, i: number) =>
        i === index
          ? {
              ...item,
              recipientName: type === "name" ? newValue : item.recipientName,
              recipientFaxNumber: type === "number" ? newValue : item.recipientFaxNumber,
            }
          : item
    );
    form.setFieldsValue({ recipients: updatedRecipients });

    setValues((prev) => {
      const newRecipients = [...prev.recipients];
      newRecipients[index] = {
        ...newRecipients[index],
        ...(type === "name"
          ? { recipientName: newValue }
          : { recipientFaxNumber: newValue }),
        contactId: undefined, // Clear contact selection on manual edit
      };
      return { ...prev, recipients: newRecipients };
    });

    latestSearchValue.current = newValue;

    if (!newValue || newValue.trim() === "") {
      setRecipientOptions([]);
      return;
    }

    const filter =
      type === "name" ? { name_query: newValue } : { fax_number: newValue };
    onGetContacts(filter, newValue, index);
  };

  const addRecipient = async () => {
    const recipients = values.recipients;
    const lastIndex = recipients?.length - 1;
    const lastRecipient = recipients[lastIndex];

    // Check if last recipient's number is empty
    if (!lastRecipient.recipientFaxNumber) {
      // Trigger validation for the last recipient's fax number field
      try {
        await form.validateFields([
          ["recipients", lastIndex, "recipientFaxNumber"],
        ]);
      } catch (err) {
        console.log('[ERROR]',err)
      }
      return; // Do not add a new recipient
    }
    setValues((prev) => {
      const newRecipients = [
        ...prev.recipients,
        {
          recipientName: undefined,
          recipientFaxNumber: undefined,
          searchInputName: undefined,
          searchInputNumber: undefined,
          contactId: undefined,
        },
      ];
      form.setFieldsValue({
        recipients: newRecipients.map((r) => ({
          recipientName: r.recipientName,
          recipientFaxNumber: r.recipientFaxNumber,
        })),
      });
      return { ...prev, recipients: newRecipients };
    });
  };

  const removeRecipient = (indexToRemove: number) => {
    setValues((prev) => {
      const newRecipients = prev.recipients.filter(
        (_, idx) => idx !== indexToRemove
      )
      form.setFieldsValue({
        recipients: newRecipients.map((r) => ({
          recipientName: r.recipientName,
          recipientFaxNumber: r.recipientFaxNumber
        }))
      })

      return { ...prev, recipients: newRecipients }
    })
  }

  /**
   * Validates recipient fields and inserts contact if valid.
   * @param form - Ant Design Form instance
   * @param index - Index of the recipient
   */
  const handleInsertContact = async (
    form: FormInstance,
    index: number,
    insertContact: (faxNumber: string, name: string, idx: number) => void
  ) => {
    const recipients = form.getFieldValue('recipients') ?? [];
    const currentRecipient = recipients[index];
  
    // Check if both fields are filled
    if (!currentRecipient.recipientName || !currentRecipient.recipientFaxNumber) {
      // Trigger validation for these fields
      try {
        await form.validateFields([
          ["recipients", index, "recipientName"],
          ["recipients", index, "recipientFaxNumber"],
        ]);
      } catch (error) {
        console.log(error)
      }
      return;
    }
  
    // If both are filled, proceed
    insertContact(
      currentRecipient.recipientFaxNumber,
      currentRecipient.recipientName,
      index
    );
  };

  return (
    <ComposeFaxStyle>
      <ComposeFaxHeader>
        <Typography.Title level={5}>Compose a Fax</Typography.Title>
      </ComposeFaxHeader>
      <div className="compose-fax">
        <Form
          form={form}
          name="compose-fax"
          onFinish={onSubmit}
          layout="vertical"
          initialValues={values}
        >
          <Row>
            <Col flex="auto">
              <div className="compose-fax-content">
                <div className="form-group">
                  <label htmlFor="senderName" className="form-label">
                    Sender
                  </label>
                  <Row gutter={20} className="">
                    <Col xs={24} lg={12}>
                      <div className="form-group ">
                        <Form.Item
                          label="Name"
                          name="cover_from_name"
                          rules={[
                            {
                              required: true,
                              message: "Please enter sender name!",
                            },
                          ]}
                        >
                          <Input placeholder="Name" className="" required />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col xs={24} lg={12}>
                      <div className="form-group ">
                        <Form.Item
                          label="Fax Number"
                          name="from_number"
                          rules={[
                            {
                              required: true,
                              message: "Please enter sender fax number!",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Fax Number"
                            className=""
                            required
                            disabled
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                </div>
                <div className="form-group shadow-wrap">
                  <div className="shadow">
                    <label htmlFor="recipientName" className="form-label">
                      Recipient
                    </label>
                    {values.recipients.map((recipient,index) => (
                      <Row gutter={20} key={`${index}-${index}`}>
                        <Col xs={11} lg={11}>
                          <Form.Item
                            label="Name"
                            name={["recipients", index, "recipientName"]}
                          >
                            <AutoComplete
                              placeholder="Enter recipient name"
                              value={recipient.recipientName ?? ""}
                              onSearch={(searchValue) =>
                                handleRecipientInput(searchValue, "name", index)
                              }
                              onSelect={(_, option: any) =>
                                handleSelectContact(option.contactId, index)
                              }
                              options={(recipientOptions ?? []).map((opt) => ({
                                value: `${opt.firstname} ${opt.lastname}`.trim(),
                                label: renderRecipientOptionLabel(opt),
                                contactId: opt.contact_id,
                              }))}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={11} lg={11}>
                          <Form.Item
                            label="Fax Number"
                            name={["recipients", index, "recipientFaxNumber"]}
                            required={true}
                            rules={[
                              {
                                validator: () => {
                                  const selectedRecipient =
                                    values.recipients[index];
                                  const selectedRecipientFaxValue =
                                    selectedRecipient?.recipientFaxNumber ?? "";
                                  if (!selectedRecipientFaxValue)
                                    return Promise.reject(
                                      new Error("Please enter fax number")
                                    );
                                  if (
                                    selectedRecipientFaxValue.charAt(0) ===
                                      "0" ||
                                    selectedRecipientFaxValue.charAt(0) === "1"
                                  ) {
                                    return Promise.reject(
                                      new Error("Fax number cannot start with 0 or 1.")
                                    );
                                  }
                                  if (
                                    !/^\d*$/.test(selectedRecipientFaxValue)
                                  ) {
                                    return Promise.reject(
                                      new Error("Only digits 0-9 are allowed.")
                                    );
                                  }
                                  if (selectedRecipientFaxValue?.length > 10) {
                                    return Promise.reject(
                                      new Error("Maximum 10 digits are allowed.")
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <AutoComplete
                              placeholder="Enter fax number"
                              value={recipient.recipientFaxNumber ?? ""}
                              onSearch={(searchValue) => {
                                if (!/^\d*$/.test(searchValue)) return;
                                handleRecipientInput(searchValue, "number", index);
                              }}
                              onSelect={(_, option: any) =>
                                handleSelectContact(option.contactId, index)
                              }
                              options={(recipientOptions ?? []).map((opt) => ({
                                value: opt.fax_number,
                                label: renderRecipientOptionLabel(opt),
                                contactId: opt.contact_id,
                              }))}
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={2}
                          lg={2}
                          className="d-flex align-item-end justify-content-start"
                        >
                          <div className="d-flex align-item-start justify-content-start h-100 form-action">
                            {showCheckbox[index] && (
                              <Tooltip title="Add to contacts">
                                <div>
                                  <Button
                                    type="text"
                                    onClick={() =>
                                      handleInsertContact(
                                        form,
                                        index,
                                        insertContact
                                      )
                                    }
                                    icon={<AddSquareIcon />}
                                  />
                                </div>
                              </Tooltip>
                            )}
                            {!!index && (
                              <Tooltip title="Delete Recipient">
                                <div>
                                  <Button
                                    type="text"
                                    onClick={() => removeRecipient(index)}
                                    icon={<RemoveSquareIcon />}
                                    className="delete-recipient-icon"
                                  />
                                </div>
                              </Tooltip>
                            )}
                          </div>
                        </Col>
                      </Row>
                    ))}

                    <Button
                      type="link"
                      size="small"
                      className="h-20 add-more-action"
                      icon={<AddSquareIcon />}
                      onClick={addRecipient}
                    >
                      Add more
                    </Button>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="addCoverPage" className="form-label">
                    Cover Page
                  </label>
                  {checkCoverPageResponse?.coverpage_id ? (
                    <div className="pt-2">
                      <Tag
                        color="blue"
                        className="text-sm py-1.5 px-3 leading-tight rounded-md"
                      >
                        Cover page will automatically be inserted by the
                        administrator.
                      </Tag>
                    </div>
                  ) : (
                    <Row gutter={20} className="">
                      <Col xs={24} lg={24}>
                        <div className="form-group ">
                          {coverPage?.coverpages?.length > 0 && (
                            <Row gutter={20} className="">
                              <Col xs={24} lg={12}>
                                <div className="form-group ">
                                  <Form.Item
                                    label="Select Cover Page"
                                    name="selectedCover"
                                  >
                                    <SelectBar
                                      options={coverpageOptions}
                                      value={values?.selectedCover}
                                      onChange={(value: any) => {
                                        setValues((prev) => ({
                                          ...prev,
                                          selectedCover: value,
                                        }));
                                      }}
                                    />
                                  </Form.Item>
                                </div>
                              </Col>
                            </Row>
                          )}
                          {!values.selectedCover && (
                            <>
                              <div className="checkbox-wrapper">
                                <Form.Item
                                  name="addCover"
                                  valuePropName="checked"
                                >
                                  <CheckBox
                                    name="addCover"
                                    onChange={handleCheckboxChange}
                                  >
                                    Add cover page
                                  </CheckBox>
                                </Form.Item>
                              </div>
                              {values?.addCover && (
                                <Form.Item name="coverPageOption">
                                  <Radio.Group
                                    className="radio-wrapper"
                                    onChange={handleRadioChange}
                                    defaultValue={false}
                                  >
                                    <Radio value={false}>Auto Generate</Radio>
                                    <Radio value={true}>Upload</Radio>
                                  </Radio.Group>
                                </Form.Item>
                              )}
                              {values.addCover &&
                                (values.isUpload ? (
                                  <Form.Item
                                    name="cover_page"
                                    valuePropName="cover_page"
                                    getValueFromEvent={(e) => e?.cover_page}
                                  >
                                    <div className="upload-wrapper compose-fax">
                                      <Upload {...coverPageUploadProps}>
                                        <Button type="primary" size="small">
                                          Cover Page Upload
                                        </Button>
                                      </Upload>
                                    </div>
                                  </Form.Item>
                                ) : (
                                  <Row gutter={20} className="">
                                    <Col xs={12} lg={12}>
                                      <div className="form-group ">
                                        <Form.Item
                                          label="Subject"
                                          name="cover_subject"
                                        >
                                          <Input
                                            placeholder="Enter subject name"
                                            className=""
                                          />
                                        </Form.Item>
                                      </div>
                                    </Col>
                                    <Col xs={12} lg={12}>
                                      <div className="form-group ">
                                        <Form.Item
                                          label="Message"
                                          name="cover_body"
                                        >
                                          <Input
                                            placeholder="Enter message"
                                            className=""
                                          />
                                        </Form.Item>
                                      </div>
                                    </Col>
                                  </Row>
                                ))}
                            </>
                          )}
                        </div>
                      </Col>
                    </Row>
                  )}
                </div>
                <div className="form-group">
                  <Row gutter={20} className="">
                    <Col xs={24} lg={24}>
                      <div className="form-group ">
                        <Form.Item label="Notes" name="notes">
                          <Input placeholder="Notes" className="" />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                </div>
                <div className="form-group upload">
                  <Row gutter={20} className="">
                    <Col xs={24} lg={24}>
                      <div className="upload-wrapper compose-fax">
                        <p className="upload-head">Upload Document <span style={{ color: "#ff4d4f" }}> *</span></p>
                        <span className="upload-details">
                          {`Support file types (Max 5 Files with 25 MB total): PDF, TIF, TIFF, Microsoft Word (.doc, .docx). If needed, compress your `}
                          <a
                            href="https://www.adobe.com/acrobat/online/compress-pdf.html"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline"
                          >
                            pdf here
                          </a>
                        </span>
                        <Form.Item
                          name="fax_file"
                          valuePropName="fax_file"
                          getValueFromEvent={(e) => e?.fax_file}
                          rules={[
                            {
                              required: true,
                              message: "Please upload a document!",
                            },
                          ]}
                        >
                          <Upload {...faxFileUploadProps}>
                            <Button type="primary" size="small">
                              Select Document
                            </Button>
                          </Upload>
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
            <Col flex="527px">
              <div className="preview-wrapper">
                <PdfViewer selectFile={values.fax_file} />
              </div>
            </Col>
          </Row>
          <div className="compose-fax-footer">
            <Row>
              <Col xs={24} lg={12} />
              <Col xs={24} lg={12}>
                <div className="footerright">
                  <Button
                    size="small"
                    onClick={() => navigate(-1)}
                    disabled={isSending}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="primary"
                    size="small"
                    htmlType="submit"
                    loading={isSending}
                  >
                    Send
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        </Form>
      </div>
    </ComposeFaxStyle>
  );
};

export default ComposeFax;
